import { useCallback } from 'react';
import {
  getCollapsedState,
  useCollapsedActions,
} from '@components/MainStage/collapsedLayers.store';
import useCollapseController from '@components/MainStage/hooks/useCollapseController';
import { LayerTypes, SubLayerTypes } from '@constants/canvas/layers';
import { collapseRelationController } from '@utils/canvas/CollapseRelationController';

/**
 * Reveals collapsed layers/sub-layers so multi-selected entities are visible on
 * the canvas. Used by Select All (expand everything) and by list multi-select
 * of an entity that lives inside a collapsed layer.
 */
const useExpandCollapsedLayers = () => {
  const { expandLayer } = useCollapseController();
  const { isCollapsed, expand, reset } = useCollapsedActions();

  const expandAll = useCallback(async () => {
    const collapsedIds = [...getCollapsedState().collapsed];

    if (collapsedIds.length === 0) return;

    // Mark everything expanded first: node visibility in `expandLayer` depends
    // on the collapsed state of each node's sub-layer.
    reset();

    await expandLayer(collapsedIds);
    await collapseRelationController.renderPhantoms();
  }, [expandLayer, reset]);

  const expandForNode = useCallback(
    async (layerId: LayerTypes, subLayerId: SubLayerTypes) => {
      const idsToExpand: (LayerTypes | SubLayerTypes)[] = [];

      if (isCollapsed(layerId)) idsToExpand.push(layerId);
      if (isCollapsed(subLayerId)) idsToExpand.push(subLayerId);

      if (idsToExpand.length === 0) return;

      idsToExpand.forEach((id) => expand(id));

      await expandLayer(idsToExpand);
      await collapseRelationController.renderPhantoms();
    },
    [isCollapsed, expand, expandLayer],
  );

  return { expandAll, expandForNode };
};

export default useExpandCollapsedLayers;
